import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { formatDateTime } from '../shared/formatting';
import { DateInput } from '../shared/DateInput';

type AuditRow = {
    id: number;
    user_id: number | null;
    user_name: string | null;
    action: string;
    method: string;
    route: string;
    target_table: string | null;
    target_id: string | null;
    target_label: string | null;
    payload: string | null;
    response_status: number | null;
    ip_address: string | null;
    user_agent: string | null;
    created_at: string;
};

type Payload = {
    rows: AuditRow[];
    total: number;
    page: number;
    limit: number;
    filters: {
        users: Array<{ user_id: number; user_name: string }>;
        actions: string[];
        tables: string[];
    };
};

export function AdminAuditLogSection({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const def = adminSectionDefinition('160');
    const title = def ? adminSectionNavLabel(def, locale) : '160';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [payload, setPayload] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filterUserId, setFilterUserId] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterTable, setFilterTable] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const q = new URLSearchParams();
            q.set('page', String(page));
            q.set('limit', '100');
            if (search) q.set('search', search);
            if (filterUserId) q.set('user_id', filterUserId);
            if (filterAction) q.set('action', filterAction);
            if (filterTable) q.set('target_table', filterTable);
            if (filterDateFrom) q.set('date_from', filterDateFrom);
            if (filterDateTo) q.set('date_to', filterDateTo);
            const r = await api<Payload>(`/admin/audit-log?${q}`, token);
            setPayload(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, page, search, filterUserId, filterAction, filterTable, filterDateFrom, filterDateTo]);

    useEffect(() => { void load(); }, [load]);

    const total = payload?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / 100));

    const actionColor: Record<string, string> = {
        create: '#1f7a36',
        update: 'var(--ad-acc)',
        delete: '#b14040',
        lock: 'var(--ad-ink)',
        unlock: 'var(--ad-acc-3)',
        archive: '#888',
        restore: '#1f7a36',
        impersonate: '#b14040',
        rollback: '#b14040',
        snapshot_refresh: '#4b6ea5',
    };

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🕵</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">
                        {t(
                            'Sve uspješne admin write operacije (POST/PATCH/DELETE) sustav zapisuje. Filtriraj po useru, akciji, tablici ili rasponu datuma. Klik na red otkriva sirovi payload.',
                            'All successful admin writes (POST/PATCH/DELETE) are logged. Filter by user, action, table or date range. Click a row to see the raw payload.',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
                <label className="filter-label">
                    {t('Korisnik', 'User')}
                    <select value={filterUserId} onChange={(e) => { setFilterUserId(e.target.value); setPage(1); }}>
                        <option value="">{t('Svi', 'All')}</option>
                        {(payload?.filters.users ?? []).map((u) => (
                            <option key={u.user_id} value={u.user_id}>{u.user_name}</option>
                        ))}
                    </select>
                </label>
                <label className="filter-label">
                    {t('Akcija', 'Action')}
                    <select value={filterAction} onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}>
                        <option value="">{t('Sve', 'All')}</option>
                        {(payload?.filters.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </label>
                <label className="filter-label">
                    {t('Tablica', 'Table')}
                    <select value={filterTable} onChange={(e) => { setFilterTable(e.target.value); setPage(1); }}>
                        <option value="">{t('Sve', 'All')}</option>
                        {(payload?.filters.tables ?? []).map((t2) => <option key={t2} value={t2}>{t2}</option>)}
                    </select>
                </label>
                <label className="filter-label">
                    {t('Od', 'From')}
                    <DateInput value={filterDateFrom} onChange={(iso) => { setFilterDateFrom(iso); setPage(1); }} />
                </label>
                <label className="filter-label">
                    {t('Do', 'To')}
                    <DateInput value={filterDateTo} onChange={(iso) => { setFilterDateTo(iso); setPage(1); }} />
                </label>
                <label className="filter-label" style={{ flex: 1, minWidth: 200 }}>
                    {t('Pretraga', 'Search')}
                    <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder={t('ime / ruta / id / payload', 'name / route / id / payload')} />
                </label>
                <button type="button" className="secondary compact" onClick={() => {
                    setSearch(''); setFilterUserId(''); setFilterAction(''); setFilterTable(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(1);
                }}>{t('Resetiraj', 'Reset')}</button>
            </div>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {!loading && payload ? (
                <>
                    <p className="muted" style={{ fontSize: '0.8rem' }}>
                        {t('Ukupno', 'Total')}: <strong>{total}</strong> · {t('stranica', 'page')} {page}/{totalPages}
                    </p>
                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>{t('Vrijeme', 'Time')}</th>
                                    <th>{t('Korisnik', 'User')}</th>
                                    <th>{t('Akcija', 'Action')}</th>
                                    <th>{t('Tablica', 'Table')}</th>
                                    <th>{t('Ruta', 'Route')}</th>
                                    <th>{t('Status', 'Status')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {payload.rows.length === 0 ? (
                                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1rem', fontStyle: 'italic', color: 'rgba(0,0,0,0.5)' }}>
                                        {t('Nema zapisa za odabrane filtere.', 'No entries for the selected filters.')}
                                    </td></tr>
                                ) : null}
                                {payload.rows.map((r) => {
                                    const color = actionColor[r.action] ?? '#666';
                                    return (
                                        <React.Fragment key={r.id}>
                                            <tr>
                                                <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{formatDateTime(r.created_at, locale)}</td>
                                                <td>{r.user_name ?? <span className="muted">—</span>}</td>
                                                <td>
                                                    <span style={{ background: color + '20', color, padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                        {r.method} · {r.action}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '0.82rem' }}>
                                                    {r.target_table ? <>{r.target_table}{r.target_id ? <span className="muted">#{r.target_id}</span> : null}</> : <span className="muted">—</span>}
                                                </td>
                                                <td style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{r.route}</td>
                                                <td><span style={{ color: r.response_status && r.response_status >= 300 ? '#b14040' : '#1f7a36' }}>{r.response_status ?? '?'}</span></td>
                                                <td>
                                                    <button type="button" className="secondary compact" style={{ padding: '2px 8px' }} onClick={() => setExpandedRowId((c) => c === r.id ? null : r.id)}>
                                                        {expandedRowId === r.id ? '▾' : '▸'} {t('Payload', 'Payload')}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedRowId === r.id ? (
                                                <tr>
                                                    <td colSpan={7} style={{ background: 'rgba(var(--ad-soft-rgb),0.06)', padding: '10px 14px' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', fontSize: '0.78rem', marginBottom: 8 }}>
                                                            <div><span className="muted">IP:</span> {r.ip_address ?? '—'}</div>
                                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><span className="muted">UA:</span> {r.user_agent ? r.user_agent.slice(0, 70) : '—'}</div>
                                                            {r.target_label ? <div><span className="muted">Target:</span> {r.target_label}</div> : null}
                                                        </div>
                                                        <pre style={{ margin: 0, padding: '8px 10px', background: 'var(--ad-bg)', borderRadius: 6, fontSize: '0.75rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace', maxHeight: 280, overflowY: 'auto' }}>
                                                            {r.payload ? (() => { try { return JSON.stringify(JSON.parse(r.payload), null, 2); } catch { return r.payload; } })() : '—'}
                                                        </pre>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 1 ? (
                        <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'center' }}>
                            <button type="button" className="secondary compact" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ {t('Prethodna', 'Prev')}</button>
                            <span style={{ alignSelf: 'center', fontSize: '0.85rem' }}>{page} / {totalPages}</span>
                            <button type="button" className="secondary compact" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{t('Sljedeća', 'Next')} ›</button>
                        </div>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}
